import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { filesystemAPI, conversationDirectoryAPI } from '../../services/api';

interface ScriptPreviewProps {
  conversationId?: string;
  className?: string;
}

export const ScriptPreview: React.FC<ScriptPreviewProps> = ({ 
  conversationId, 
  className = "" 
}) => {
  const [scriptContent, setScriptContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setLastModified] = useState<string | null>(null);

  useEffect(() => {
    const fetchScriptContent = async () => {
      if (!conversationId) {
        setScriptContent('');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Get the conversation's working directory
        const dirResponse = await conversationDirectoryAPI.get(conversationId);
        const workingDirectory = dirResponse.workingDirectory;
        
        if (!workingDirectory) {
          setError('No working directory set for this conversation');
          setScriptContent('');
          return;
        }
        
        // Construct the full path to voiceover-script.txt
        const scriptPath = workingDirectory.endsWith('/') 
          ? `${workingDirectory}voiceover-script.txt`
          : `${workingDirectory}/voiceover-script.txt`;
        
        // Try to read voiceover-script.txt from the working directory
        const response = await filesystemAPI.read(scriptPath);
        setScriptContent(response.content);
        
        // Set a mock last modified date for now
        setLastModified(new Date().toISOString());
        
      } catch (err) {
        console.warn('Could not load voiceover-script.txt:', err);
        setError('Script file not found or could not be loaded');
        setScriptContent('');
      } finally {
        setLoading(false);
      }
    };

    fetchScriptContent();
  }, [conversationId]);

  const refreshScript = () => {
    if (conversationId) {
      const fetchScript = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // Get the conversation's working directory
          const dirResponse = await conversationDirectoryAPI.get(conversationId);
          const workingDirectory = dirResponse.workingDirectory;
          
          if (!workingDirectory) {
            setError('No working directory set for this conversation');
            setScriptContent('');
            return;
          }
          
          // Construct the full path to voiceover-script.txt
          const scriptPath = workingDirectory.endsWith('/') 
            ? `${workingDirectory}voiceover-script.txt`
            : `${workingDirectory}/voiceover-script.txt`;
          
          const response = await filesystemAPI.read(scriptPath);
          setScriptContent(response.content);
          setLastModified(new Date().toISOString());
        } catch (err) {
          setError('Could not refresh script content');
        } finally {
          setLoading(false);
        }
      };
      fetchScript();
    }
  };

  const formatTextForDisplay = (content: string): string => {
    // Basic text formatting for plain text display
    return content
      .replace(/\n\n/g, '</p><p class="mb-4 text-foreground leading-relaxed">')
      .replace(/^\n?/, '<p class="mb-4 text-foreground leading-relaxed">')
      + '</p>';
  };

  if (loading) {
    return (
      <div className={`h-full flex items-center justify-center bg-background ${className}`}>
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-muted-foreground/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-sm font-medium mb-1">Loading Script</div>
          <div className="text-xs text-muted-foreground">Fetching voiceover-script.md...</div>
        </div>
      </div>
    );
  }

  if (error || !scriptContent) {
    return (
      <div className={`h-full flex items-center justify-center bg-background ${className}`}>
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2">No Script Found</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {error || 'No voiceover-script.txt file found in the current working directory.'}
          </p>
          <Button 
            onClick={refreshScript} 
            variant="outline" 
            size="sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col bg-background ${className}`}>
      {/* Ultra-thin Header */}
      <div className="flex-shrink-0 h-12 bg-card/30 backdrop-blur-sm border-b border-border/50 flex items-center px-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary/10 rounded-md flex items-center justify-center">
              <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-sm font-medium">Voice-over Script</h1>
            
            {/* Compact inline stats */}
            <div className="flex items-center gap-3 ml-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="font-medium text-primary">{scriptContent.split('\n').length}</span>
                lines
              </span>
              <span className="flex items-center gap-1">
                <span className="font-medium text-primary">{scriptContent.split(/\s+/).filter(word => word.length > 0).length}</span>
                words
              </span>
              <span className="flex items-center gap-1">
                <span className="font-medium text-primary">{Math.ceil(scriptContent.length / 1000)}k</span>
                chars
              </span>
              <span className="flex items-center gap-1">
                <span className="font-medium text-primary">{Math.ceil(scriptContent.split(/\s+/).filter(word => word.length > 0).length / 200)}</span>
                min read
              </span>
            </div>
          </div>
          <Button 
            onClick={refreshScript} 
            variant="ghost" 
            size="sm" 
            className="h-7 px-2 text-xs"
          >
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </Button>
        </div>
      </div>

      {/* Script-focused Layout */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-br from-background via-background to-muted/5">
        <div className="max-w-4xl mx-auto">
          {/* Main Script Content - Premium Focus */}
          <div className="px-6 py-8">
            <div className="relative bg-white dark:bg-card/80 rounded-2xl border border-border/30 overflow-hidden shadow-lg backdrop-blur-sm">
              {/* Elegant header with gradient */}
              <div className="relative px-8 py-6 bg-gradient-to-r from-primary/5 via-primary/3 to-transparent border-b border-border/20">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.02] to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-lg text-foreground mb-1">Script Content</h2>
                    <p className="text-sm text-muted-foreground">Professional narration content ready for production</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 px-3 py-1.5 rounded-full font-medium border border-emerald-200/50 dark:border-emerald-800/50">
                      ✓ Ready for Production
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Premium script content area */}
              <div className="px-10 py-12">
                {/* Elegant drop cap and typography */}
                <article className="prose prose-lg dark:prose-invert max-w-none">
                  <div 
                    className="relative text-foreground font-['Inter',system-ui,sans-serif] [&>p]:mb-8 [&>p]:text-[17px] [&>p]:leading-[1.75] [&>p:first-child]:text-[20px] [&>p:first-child]:font-medium [&>p:first-child]:text-primary [&>p:first-child]:mb-10 [&>p:first-child]:leading-[1.6] [&>p:first-child]:tracking-[-0.01em] [&>p]:tracking-[-0.005em] [&>p]:text-slate-700 [&>p]:dark:text-slate-200 selection:bg-primary/10"
                    dangerouslySetInnerHTML={{ 
                      __html: formatTextForDisplay(scriptContent) 
                    }}
                  />
                </article>
                
                {/* Subtle reading progress indicator */}
                <div className="mt-12 pt-8 border-t border-border/20">
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <div className="w-1 h-1 bg-primary/40 rounded-full"></div>
                    <span>End of Script</span>
                    <div className="w-1 h-1 bg-primary/40 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Elegant metadata footer with glass morphism */}
          <div className="px-6 pb-8">
            <div className="relative">
              {/* Background blur element */}
              <div className="absolute inset-0 bg-gradient-to-r from-card/40 via-card/60 to-card/40 backdrop-blur-sm rounded-xl border border-border/30"></div>
              
              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
                {/* Enhanced Content Analysis */}
                <div className="group">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg flex items-center justify-center group-hover:from-primary/15 group-hover:to-primary/8 transition-all duration-200">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">Content Analysis</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-muted-foreground">Readability</span>
                      <span className="text-sm font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 px-2 py-1 rounded-md">Professional</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-muted-foreground">Speech duration</span>
                      <span className="text-sm font-medium text-foreground">{Math.ceil(scriptContent.split(/\s+/).filter(word => word.length > 0).length / 150)} minutes</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-muted-foreground">Complexity</span>
                      <span className="text-sm font-medium text-foreground">Medium</span>
                    </div>
                  </div>
                </div>

                {/* Enhanced File Details */}
                <div className="group">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 bg-primary/10 group-hover:bg-primary/15">
                      <svg className="w-4 h-4 text-primary dark:text-accent-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">File Details</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-muted-foreground">Format</span>
                      <span className="text-sm font-medium font-mono bg-muted/50 px-2 py-1 rounded text-foreground">Markdown</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-muted-foreground">Voice compatibility</span>
                      <span className="text-sm font-medium text-foreground">All premium voices</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-muted-foreground">Export ready</span>
                      <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">✓ Yes</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};